/**
 * Click Effects - shared runtime.
 *
 * Owns the canvas, the render loop, the event wiring and the public API.
 * The actual effects live in separate bundles (src/js/modules/click-effects/<id>.js)
 * and register themselves into window.__myproClickEffectsRegistry as
 * { spawn(x, y, useColor) } objects.
 *
 * Bundle split rationale: the frontend loads the runtime plus ONLY the
 * selected effect's bundle (the module picks it from the saved setting), so
 * the shipped JS contains exactly one effect implementation. The admin
 * settings screen loads the runtime plus ALL effect bundles so the Test
 * button can preview any dropdown choice.
 *
 * Config (window.myproClickEffects, set by the module via wp_localize_script):
 *   effect          - selected effect id
 *   color           - hex color applied to every effect
 *   trigger         - 'interactive' (buttons & links only) | 'any'
 *   disableOnMobile - skip entirely on coarse-pointer devices
 *   adminPreview    - admin settings screen mode: no global listeners, API only
 *
 * Build entry: src/js/modules/click-effects/runtime.js
 * Output:      assets/js/modules/click-effects-runtime.min.js
 */
(() => {
	

	var config = window.myproClickEffects || {};

	var effectId = config.effect || 'sonar';
	var defaultColor = config.color || '#ffffff';
	var trigger = config.trigger === 'any' ? 'any' : 'interactive';

	// Touch-first sites have no hover and no cursor; click effects there are
	// noise. The admin can opt back in by turning the toggle off. Skipped in
	// admin preview mode (the test button must work everywhere).
	if (!config.adminPreview && config.disableOnMobile && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
		return;
	}

	// ------------------------------------------------------------------
	// Effect registry - populated by the per-effect bundles. The systems
	// array lives on the same shared object so the split bundles can push
	// particles into the loop that the runtime drives.
	// ------------------------------------------------------------------

	var effects = window.__myproClickEffectsRegistry || (window.__myproClickEffectsRegistry = {});
	var systems = effects.systems || (effects.systems = []);

	// ------------------------------------------------------------------
	// Shared canvas + render loop (one per page).
	// ------------------------------------------------------------------

	var canvas = null;
	var ctx = null;
	var running = false;

	function ensureCanvas() {
		if (canvas) {
			return;
		}

		canvas = document.createElement('canvas');
		canvas.setAttribute('aria-hidden', 'true');
		canvas.style.position = 'fixed';
		canvas.style.top = '0';
		canvas.style.left = '0';
		canvas.style.width = '100vw';
		canvas.style.height = '100vh';
		canvas.style.pointerEvents = 'none';
		canvas.style.zIndex = '9999';
		document.body.appendChild(canvas);

		syncSize();
		window.addEventListener('resize', syncSize);

		ctx = canvas.getContext('2d');
	}

	function syncSize() {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
	}

	function startLoop() {
		if (running) {
			return;
		}
		running = true;

		var draw = () => {
			if (systems.length === 0) {
				ctx.clearRect(0, 0, canvas.width, canvas.height);
				running = false;
				return;
			}

			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// update() returns false once the particle is dead. Filter IN
			// PLACE - the array is shared with the effect bundles, so
			// replacing it (Array#filter returns a new array) would orphan
			// every particle pushed by later spawns.
			for (var i = systems.length - 1; i >= 0; i--) {
				if (!systems[i].update(ctx)) {
					systems.splice(i, 1);
				}
			}

			requestAnimationFrame(draw);
		};

		requestAnimationFrame(draw);
	}

	/**
	 * Spawn an effect at viewport coordinates.
	 *
	 * clientX/clientY map directly to the fixed canvas - no rect offset.
	 * Effect id and color default to the localized config, but can be
	 * overridden per call (used by the admin test button, which previews
	 * unsaved form values).
	 *
	 * No-op when the requested effect bundle is not loaded - on the frontend
	 * that cannot happen (PHP enqueues the selected effect's bundle), on the
	 * admin page all bundles are loaded.
	 */
	function spawn(x, y, effectOverride, colorOverride) {
		var id = effectOverride || effectId;
		var fx = effects[id];
		if (!fx) {
			return;
		}
		var col = colorOverride || defaultColor;
		ensureCanvas();
		fx.spawn(x, y, col);
		startLoop();
	}

	// ------------------------------------------------------------------
	// Public API + event wiring.
	//
	// MyProClickEffects.play(x, y, { effect, color }) lets external code
	// (e.g. the admin test button) fire any effect with any color at any
	// point, bypassing the global listeners.
	//
	// In admin preview mode (config.adminPreview) the global listeners are
	// NOT wired - the admin page drives effects exclusively through the API,
	// so normal admin clicks (Save Changes, nav links) stay quiet.
	// ------------------------------------------------------------------

	window.MyProClickEffects = {
		play: (x, y, opts) => {
			opts = opts || {};
			spawn(x, y, opts.effect, opts.color);
		},
	};

	if (!config.adminPreview) {
		// pointerdown (primary button) fires before click and therefore before
		// navigation, so a link click always shows the effect even when the page
		// unloads right after. Capture phase keeps the effect working when
		// third-party scripts stop propagation on the click.
		//
		// Keyboard activation (Enter/Space on a focused button/link) dispatches
		// a click with detail === 0 and no pointerdown - handled separately,
		// spawning at the element center.

		var INTERACTIVE_SELECTOR =
			'a, button, [role="button"], input[type="submit"], input[type="button"], input[type="reset"], select, summary, [onclick]';

		function isTargetInteractive(target) {
			return !!(target && target.closest && target.closest(INTERACTIVE_SELECTOR));
		}

		function shouldFire(target) {
			if (trigger === 'any') {
				return true;
			}
			return isTargetInteractive(target);
		}

		document.addEventListener(
			'pointerdown',
			(e) => {
				if (typeof e.button === 'number' && e.button !== 0) {
					return;
				}
				if (!shouldFire(e.target)) {
					return;
				}
				spawn(e.clientX, e.clientY);
			},
			true
		);

		document.addEventListener(
			'click',
			(e) => {
				// detail > 0 means a real pointer click - already handled above.
				if (e.detail !== 0) {
					return;
				}
				if (!shouldFire(e.target)) {
					return;
				}
				var rect = e.target.getBoundingClientRect();
				spawn(rect.left + rect.width / 2, rect.top + rect.height / 2);
			},
			true
		);
	}
})();
